/**
 * FocusedRedeemShell — full-viewport focused takeover for the invite-code
 * journey (2026-08-02 owner feedback: the account question appeared mid-way
 * down the pricing page surrounded by marketing copy, so the user didn't
 * know where to focus). Once a code is validated, every subsequent step of
 * the journey renders inside this quiet, single-purpose surface: wordmark,
 * journey stepper, one panel, nothing else.
 *
 * Used by /pricing and /start so both entry points stay pixel-consistent.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';
import BrandWordmark from '../BrandWordmark';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const Shell = styled.div`
  position: fixed;
  inset: 0;
  /* Above the chat widget (500000) — the focused journey owns the screen. */
  z-index: 600001;
  background: var(--brand-canvas);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/* One width token for the top bar AND the content column — they used to be
   720px vs 560px, so the wordmark sat 80px outboard of the panel on every
   desktop screen (2026-08-03 desktop-polish audit). */
const shellWidthLadder = `
  max-width: 560px;

  ${media.laptop} {
    max-width: 660px;
  }

  ${media.desktop} {
    max-width: 760px;
  }
`;

/* Rail variant (2026-08-04 owner consistency directive): steps 1–2 used a
   narrow centered strip while steps 3–6 run the rail-grid onboarding layout,
   so the journey visibly changed shape mid-flow. With a `rail` node the shell
   adopts the SAME skeleton — stepper rail on the left, one content card on
   the right — at the same widths as the onboarding grid. Mobile stacks. */
const railWidthLadder = `
  max-width: 560px;

  ${media.laptop} {
    max-width: 1020px;
  }

  ${media.desktop} {
    max-width: 1100px;
  }
`;

const TopBar = styled.div<{ $rail?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.25rem;
  ${p => (p.$rail ? railWidthLadder : shellWidthLadder)}
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;


const BackButton = styled.button`
  background: none;
  border: none;
  font-family: inherit;
  font-size: 0.8rem;
  color: #9A948A;
  cursor: pointer;
  text-transform: lowercase;
  padding: 0.35rem 0.4rem;

  &:hover { color: #555; text-decoration: underline; }
`;

const Column = styled.div`
  width: 100%;
  ${shellWidthLadder}
  margin: 0 auto;
  padding: 1.25rem 1.25rem 3rem;
  box-sizing: border-box;
  display: grid;
  gap: 0.9rem;
  align-content: start;
  flex: 1;

  /* On a tall desktop viewport, a top-pinned panel leaves ~600px of void
     below it — center the step vertically instead (mobile keeps top-align
     so the panel doesn't jump around above the keyboard). */
  ${media.laptop} {
    align-content: center;
    padding-bottom: 12vh;
  }
`;

/* Same skeleton as the onboarding TrialGrid: stepper rail left, one content
   column right; stacks on mobile (the stepper renders its horizontal strip
   there). */
const RailGrid = styled.div`
  width: 100%;
  ${railWidthLadder}
  margin: 0 auto;
  padding: 1.25rem 1.25rem 3rem;
  box-sizing: border-box;
  display: grid;
  gap: 0.9rem;
  align-content: start;
  flex: 1;

  ${media.laptop} {
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 1.5rem;
    align-content: center;
    align-items: start;
    padding-bottom: 12vh;
  }
`;

const RailSlot = styled.div`
  display: grid;
  gap: 0.9rem;
  align-content: start;
`;

const RailMain = styled.div`
  display: grid;
  gap: 0.9rem;
  align-content: start;
  min-width: 0;
`;

export interface FocusedRedeemShellProps {
  children: React.ReactNode;
  /** Renders a quiet "← back" that exits the focused flow. Omit to hide. */
  onBack?: () => void;
  backLabel?: string;
  /**
   * Left-rail content (typically the JourneyStepper with railOnDesktop).
   * When provided, the shell renders the SAME rail-grid skeleton as the
   * onboarding phases so every step of the journey shares one layout.
   */
  rail?: React.ReactNode;
}

export const FocusedRedeemShell: React.FC<FocusedRedeemShellProps> = ({
  children,
  onBack,
  backLabel = '← back',
  rail,
}) => {
  // Lock background scroll while the focused flow is up — shared
  // reference-counted lock (2026-08-12 scrolling round) so overlapping
  // overlays can't restore each other's snapshots out of order.
  useBodyScrollLock(true);

  // Portal to <body> (2026-08-02 review, CRITICAL): rendered inline, any
  // transformed/filtered ancestor (page animations) becomes the fixed
  // containing block — the takeover collapses into the page while the body
  // scroll stays locked. The portal guarantees viewport-relative fixed.
  return createPortal(
    <Shell role="dialog" aria-modal="true" data-testid="focused-redeem-shell">
      <TopBar $rail={!!rail}>
        <BrandWordmark href="/" />
        {onBack && (
          <BackButton type="button" onClick={onBack}>{backLabel}</BackButton>
        )}
      </TopBar>
      {rail ? (
        <RailGrid>
          <RailSlot>{rail}</RailSlot>
          <RailMain>{children}</RailMain>
        </RailGrid>
      ) : (
        <Column>{children}</Column>
      )}
    </Shell>,
    document.body,
  );
};

export default FocusedRedeemShell;
